import React, {useCallback, useEffect, useMemo, useState, useContext} from 'react';
import PropTypes from 'prop-types';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination , TableRow, TableSortLabel, styled, tableCellClasses, Paper, Backdrop, Typography, IconButton, 
    CircularProgress, TextField, InputAdornment, Button, Stack,
    Dialog,
    MenuItem} from '@mui/material';
import { Edit, DeleteForever, Search, Add, Upload } from '@mui/icons-material';
import Header from '../Components/ui/Header';
import DonateurForm from '../Components/forms/donateur-form';
import { API } from "../conf";
import { Context } from "../App";
import EditDonateurForm from '../Components/forms/edit-donator';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
}));

function descendingComparator(a, b) {
  if (b < a) {
    return -1;
  }
  if (b > a) {
    return 1;
  }
  return 0;
}

function getComparator(order) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b);
}

function stableSort(array, table, orderBy, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = orderBy >-1 ? comparator(table[orderBy]?.sortBy(a[0]), table[orderBy]?.sortBy(b[0])) : 0;
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, table } =    props;
    
    const createSortHandler = (property) => (event) => onRequestSort(event, property)

    return (
        <TableHead>
        <StyledTableRow>
            {table.map((col, index) => (
            <StyledTableCell
                key={index}
                align='center'
                sx={{fontWeight:'bold'}}
                sortDirection={orderBy === index ? order : false}
            >
                    {col.sortable? <TableSortLabel
                    active={orderBy === index}
                    direction={orderBy === index ? order : 'asc'}
                    onClick={createSortHandler(index)}
                    >
                        {col.header}
                    </TableSortLabel> : col.header}
            </StyledTableCell>
            ))}
        </StyledTableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.number.isRequired,
};

export default function DonatorManager() {
    const { updateConnect } = useContext(Context)
    const [organisation, setOrganisation] = useState(JSON.parse(sessionStorage.getItem('eglise')))
    const [agent, setAgent] = useState(JSON.parse(sessionStorage.getItem('user')))
    const [eglises, setEglises] = useState([])
    const [egliseId, setEgliseId] = useState('')
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(1);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const [loading, setLoad] = useState(true)
    const [importing, setImport] = useState(false)
    const [add, setAdd] = useState(false)
    const [del, setDel] = useState(false)
    const [edit, setEdit] = useState(false)
    const [search_input, setSearch] = useState('')
    const [donators, setDonators] = useState([])
    const [donator, setDonator] = useState()
    const table = useCallback(()=>[
        {header: "Civilité", sortable: true, sortBy:(el)=>el.genre.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{width:'60px', textTransform:'capitalize'}}>{row.genre}</StyledTableCell>},
        {header: "Nom", sortable: true, sortBy:(el)=>el.nom.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'100px', textTransform:'capitalize'}}>{row.nom}</StyledTableCell>},
        {header: "Prénoms", sortable: true, sortBy:(el)=>(el.prenom1+(Boolean(el.prenom2)?' '+el.prenom2:'')).toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'100px', textTransform:'capitalize'}}>{row.prenom1+(Boolean(row.prenom2)?' '+row.prenom2:'')}</StyledTableCell>},
        {header: "Adresse e-mail", sortable: true, sortBy:(el)=>el.mail?.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'120px'}}>{row.mail?.toLowerCase()}</StyledTableCell>},
        {header: "Téléphone", sortable: true, sortBy:(el)=>el.tel, row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'90px'}}>{row.tel}</StyledTableCell>},
        {header: "Adresse postale", sortable: true, sortBy:(el)=>el.ville?.toLowerCase(), row: (row, index)=> <StyledTableCell key={index} align="center" sx={{minWidth:'190px'}}>
            {row.voie&&<Typography variant='body1'>{row.voie}</Typography>}
            {row.adresse_complement&&<Typography variant='body1'>{row.adresse_complement}</Typography>}
            {(row.zip_code||row.ville)&&<Typography variant='body1'>{row.zip_code+' '+row.ville?.toUpperCase()}</Typography>}
        </StyledTableCell>},
        {header: "", sortable: false, row: (row, index)=><StyledTableCell key={index} align='left'>
            <IconButton aria-label="Edit" onClick={()=>{setDonator(row); setEdit(true)}} disabled={egliseId==='tous'}><Edit fontSize="small"/></IconButton>
            <IconButton aria-label="delete" onClick={()=>{setDonator(row); setDel(true)}} disabled={egliseId==='tous'&&agent.level<4}><DeleteForever color={'carmine'} fontSize="small"/></IconButton>
        </StyledTableCell>}
    ], [egliseId])

    const eglise = useMemo(()=>organisation&&organisation.type==='église' ? organisation : eglises.find(el=>el._id===egliseId), [egliseId, eglises, organisation])
    useEffect(()=>{if(organisation&&organisation.type==='église'&&eglises.some(el=>el._id===organisation._id)) sessionStorage.setItem('eglise', JSON.stringify(eglises.find(el=>el._id===organisation._id)))}, [eglises, organisation])

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function normalize(str) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-.+@]/gi,'').toLowerCase()
    }

    function get_searchable(dnt) {
        return dnt.nom+' '+dnt.prenom1+(Boolean(dnt.prenom2)?' '+dnt.prenom2:'')+' '+dnt.mail+' '+dnt.tel+' '+dnt.voie+' '+dnt.adresse_complement+' '+dnt.zip_code+' '+dnt.ville
    }

    function handleDelete(){
        setDel(false); setLoad(true)
        fetch(API.concat(`/eglises/${eglise._id}/donateurs/delete/${donator.num}`), {
            method:'PATCH',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                updateConnect(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    const json = await response.json()
                    setEglises(old=>[...old.filter(el=>el.number!==json.number), json])
                    setDonators(old=> old.filter(donat=> donat._id !== donator._id))
                    setDonator()
                }
                setLoad(false)
            }
        })
    }

    function updateDonateurs(newdonator){
        if(!donators.find(el => JSON.stringify(el)===JSON.stringify(newdonator))){
            setDonators(old => [...old, newdonator])
        }
    }

    function getAllDonators() {
        setLoad(true); setEgliseId('tous')
        fetch(API.concat('/donateurs'), {
            method:'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                updateConnect(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    const json = await response.json()
                    setDonators(json)
                }
                setLoad(false)
            }
        })
    }

    useEffect(()=>{
        if(eglise?.donateurs) {
            fetch(API.concat(`/donateurs/by/${JSON.stringify({num:{$in: eglise.donateurs}, $and:[{nom: {$nin: ['anonyme', 'Anonyme', 'ANONYME']}}, {prenom1: {$nin: ['anonyme', 'Anonyme', 'ANONYME']}}]})}`), {
                method:'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionStorage.getItem('token')
                }
            }).then(async response=>{
                const token = response.headers.get('x-auth-token')
                if(response.status === 401) {
                    sessionStorage.clear()
                    updateConnect(false)
                }else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                    }
                    if (response.status === 200) {
                        const json = await response.json()
                        setDonators(json)
                    }
                    setLoad(false)
                }
            })
        }
    }, [eglise])

    useEffect(()=>{
        if (organisation&&organisation.type==='fédération') {
            setLoad(true)
            fetch(API.concat(`/eglises/by/${JSON.stringify(agent.eglises?.length>0 ? {number:{$in: agent.eglises}}:{type: {$in: ['église', 'fédération']}})}`), {
                method:'GET',
                headers:{
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionStorage.getItem('token')
                }
            }).then(async(response) => {
                const token = response.headers.get('x-auth-token')
                if(response.status === 401) {
                    sessionStorage.clear()
                    updateConnect(false)
                }else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                    }
                    if (response.status === 200) {
                        const json = await response.json();
                        setEglises(json)
                        setLoad(false)
                    }
                }
            })
        }
    }, [organisation, agent])

    useEffect(()=>{
        setOrganisation(JSON.parse(sessionStorage.getItem('eglise')))
        setAgent(JSON.parse(sessionStorage.getItem('user')))
    },[]) 

    const visibleRows = useMemo(() => stableSort(donators.filter(dnt=> normalize(get_searchable(dnt)).includes(normalize(search_input))), table(), orderBy, getComparator(order)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [donators, table, order, orderBy, page, rowsPerPage, search_input]);

    return (
        <div style={{display:'flex', flexDirection:'column', paddingBottom:'30px'}}>
            <Header/>
            <Typography sx={{textAlign:'center', marginTop:'20px', fontSize:26}}>Gestion des Donateurs</Typography>
            {organisation&&organisation.type==='fédération'&&<TextField select size="small" label="église" sx={{alignSelf:'end',marginBottom:3, width:'25%', marginRight:3, minWidth:'200px'}} value={egliseId} onChange={e=>e.target.value==='tous'?getAllDonators():setEgliseId(old=>{if(old!==e.target.value)setDonators([]); return e.target.value})}>
                <MenuItem value={'tous'}>Tous les donateurs</MenuItem>
                {eglises.sort((a,b)=>a.nom.toLowerCase()<b.nom.toLowerCase() ? -1 : 1).map(eglise=><MenuItem key={eglise._id} value={eglise._id}>{`${eglise.nom} - ${eglise.commune.toUpperCase()}`}</MenuItem>)}
            </TextField>}
            {(eglise||donators.length>0)&&<Box sx={{paddingX:'20px', marginTop:'30px'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'20px'}}>
                    <TextField
                    size="small"
                    label="Rechercher un donateur par nom, e-mail, tel"
                    disabled={!donators?.length>0}
                    sx={{width:'60%', minWidth:'300px'}}
                    value={search_input}
                    InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Search />
                          </InputAdornment>
                        ),
                    }}
                    onChange={(event)=> setSearch(event.target.value)}
                    />
                    <Stack direction='row' spacing={1}>
                        {/* <Button variant='contained' startIcon={<Upload />} onClick={()=>setImport(true)}>Importer</Button> */}
                        <Button variant='contained' startIcon={<Add />} onClick={()=>setAdd(true)}>Ajouter</Button>
                    </Stack>
                </div>
                <Box sx={{ width: '100%', marginTop:'30px' }}>
                    <Paper sx={{ width: '100%', overflow:'hidden' }}>
                        <TableContainer>
                            <Table
                                sx={{ minWidth: 700 }}
                                aria-labelledby="tableTitle"
                            >
                                <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                table={table()}
                                />
                                <TableBody>
                                {visibleRows.map((row, index) =>
                                    <StyledTableRow key={index} >
                                        {table().map((col, index)=> col.row(row, index))}
                                    </StyledTableRow>
                                )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage='lignes par page'
                        labelDisplayedRows={({ from, to, count, page }) => { return `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`; }}
                        component="div"
                        count={donators.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, npage)=> setPage(npage)}
                        onRowsPerPageChange={(event)=> {setRowsPerPage(parseInt(event.target.value, 10)); setPage(0);}}
                        />
                    </Paper>
                </Box>
            </Box>}
            <Dialog open={add||edit} maxWidth='md' fullWidth>
                {add? <DonateurForm {...{eglise, setEglises, updateDonateurs, cancel:()=>setAdd(false), updateDonateur:(n)=>setAdd(false)}}/>:
                donator&&<Paper><EditDonateurForm {...{donator, eglise, setEglises, updateDonateurs:newdonator=>{handleDelete(); setDonators(old=> [...old, newdonator])}, cancel:()=>{setEdit(false); setDonator()}, updateDonateur:(n)=>{setEdit(false); setDonator()}}}/></Paper>}
            </Dialog>
            <Backdrop open={loading||del} >
                {loading ? <CircularProgress color="inherit" /> :
                del&&<Paper sx={{minWidth:'250px', width:'40%', paddingX:'15px', paddingY:'25px'}}>
                    <Typography variant='h5'>Supprimer un donateur</Typography>
                    <Typography variant='body1'>Voulez-vous supprimer <span style={{textTransform:'capitalize'}}>{donator.genre+' '+donator.nom+' '+donator.prenom1+(Boolean(donator.prenom2)?' '+donator.prenom2:'')}</span> de la liste des donateurs ?</Typography>
                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
                        <Button variant='outlined' color='carmine' onClick={()=>{setDel(false); setDonator()}}>Annuler</Button>
                        <Button variant='contained' color='carmine' onClick={handleDelete}>Supprimer</Button>
                    </Stack>
                </Paper>}
            </Backdrop>
        </div>
    );
}
